import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";
import { parseISO, format } from "date-fns";

import api from "../../services/api";
import { toast } from "react-toastify";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.paper
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    backgroundColor: "transparent",
    boxShadow: "none"
  },
  actionCreate: { backgroundColor: "#4caf50", color: "#fff" },
  actionUpdate: { backgroundColor: "#ff9800", color: "#fff" },
  actionDelete: { backgroundColor: "#f44336", color: "#fff" },
  preData: {
    backgroundColor: theme.palette.type === "dark" ? "#1a1a2e" : "#f5f5f5",
    color: theme.palette.type === "dark" ? "#fff" : "#333",
    padding: "10px",
    borderRadius: "8px",
    maxHeight: "300px",
    overflowY: "auto",
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap"
  }
}));

const SystemLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/system-logs", {
        params: { pageNumber }
      });
      setLogs(prev => {
        // Prevent duplicates
        const newLogs = data.logs.filter(
          newLog => !prev.find(p => p.id === newLog.id)
        );
        return [...prev, ...newLogs];
      });
      setHasMore(data.hasMore);
    } catch (err) {
      toast.error("Error al cargar logs");
    }
    setLoading(false);
  };

  const loadMore = () => {
    setPageNumber(prev => prev + 1);
  };

  const handleOpenDetails = log => {
    setSelectedLog(log);
    setModalOpen(true);
  };

  const renderActionChip = action => {
    if (action === "CREATE") return <Chip size="small" label="CREAR" className={classes.actionCreate} />;
    if (action === "UPDATE") return <Chip size="small" label="EDITAR" className={classes.actionUpdate} />;
    if (action === "DELETE") return <Chip size="small" label="ELIMINAR" className={classes.actionDelete} />;
    return <Chip size="small" label={action} />;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Logs del Sistema (Auditoría)</Title>
      </MainHeader>
      <Paper className={classes.mainPaper}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Registro inalterable de acciones realizadas por los usuarios en el sistema.
        </Typography>

        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha / Hora</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Módulo</TableCell>
                <TableCell>Registro ID</TableCell>
                <TableCell align="center">Detalles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id}>
                  <TableCell>
                    {format(parseISO(log.createdAt), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  <TableCell>{log.user ? log.user.name : "Sistema"}</TableCell>
                  <TableCell>{renderActionChip(log.action)}</TableCell>
                  <TableCell>{log.module}</TableCell>
                  <TableCell>{log.recordId}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleOpenDetails(log)}
                    >
                      Ver Cambios
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {hasMore && (
          <div style={{ textAlign: "center", marginTop: 15 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              onClick={loadMore}
            >
              Cargar más
            </Button>
          </div>
        )}
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalles del Registro</DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <div>
              <Typography variant="subtitle2" gutterBottom>
                Acción: {selectedLog.action} en {selectedLog.module} (ID: {selectedLog.recordId})
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Realizado por: {selectedLog.user ? selectedLog.user.name : "Sistema"}
              </Typography>
              
              <div style={{ display: "flex", gap: "20px", marginTop: 15 }}>
                {selectedLog.oldData && (
                  <div style={{ flex: 1 }}>
                    <Typography variant="button" color="error">Datos Anteriores (Eliminado / Modificado)</Typography>
                    <pre className={classes.preData}>
                      {JSON.stringify(JSON.parse(selectedLog.oldData), null, 2)}
                    </pre>
                  </div>
                )}
                {selectedLog.newData && (
                  <div style={{ flex: 1 }}>
                    <Typography variant="button" style={{ color: "#4caf50" }}>Datos Nuevos (Creado / Actualizado)</Typography>
                    <pre className={classes.preData}>
                      {JSON.stringify(JSON.parse(selectedLog.newData), null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default SystemLogs;
